import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import SEO from '../components/SEO';
import { useAuth } from '../context/AuthContext';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { ProjectShowcase } from '../components/ui/project-showcase';

// Icons
import {
  Star,
  DollarSign,
  Calendar,
  Tag,
  ShoppingBag,
  TrendingUp,
  BarChart3,
  Users,
  Lightbulb,
  Video,
  Megaphone,
  CalendarDays,
  Smartphone,
  Link2,
  Check,
  ArrowRight,
} from 'lucide-react';

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 60 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: 'easeOut' } }
};

const fadeInLeft = {
  hidden: { opacity: 0, x: -60 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.8, ease: 'easeOut' } }
};

const fadeInRight = {
  hidden: { opacity: 0, x: 60 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.8, ease: 'easeOut' } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.3
    }
  }
};

const pulseAnimation = {
  scale: [1, 1.05, 1],
  transition: { duration: 2, repeat: Infinity }
};

const Campaigns = () => {
  const { currentUser } = useAuth();
  const [activeCampaigns, setActiveCampaigns] = useState([]);
  const [loadingCampaigns, setLoadingCampaigns] = useState(true);
  
  // Fetch active campaigns
  useEffect(() => {
    const fetchActiveCampaigns = async () => {
      try {
        setLoadingCampaigns(true);
        
        const campaignsRef = collection(db, 'campaigns');
        const now = new Date();
        
        // Get only active campaigns - same as influencer page
        const q = query(campaignsRef, where('status', '==', 'active'));
        const querySnapshot = await getDocs(q);
        const campaigns = [];
        
        querySnapshot.forEach((doc) => {
          const campaignData = { id: doc.id, ...doc.data() };
          
          // Show campaigns that haven't ended yet, regardless of payment status for now
          const endDate = new Date(campaignData.endDate);
          
          if (endDate > now && campaigns.length < 6) {
            campaigns.push(campaignData);
          }
        });
        
        setActiveCampaigns(campaigns);
        
      } catch (error) {
        console.error('Error fetching active campaigns:', error);
        setActiveCampaigns([]);
      } finally {
        setLoadingCampaigns(false);
      }
    };

    fetchActiveCampaigns();
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <SEO
        title="Campaigns — Browse Active Influencer Marketing Campaigns"
        description="Discover active influencer marketing campaigns on Engaged Manila. Browse by category — Fashion, Beauty, Food, Fitness, Technology, Gaming, and more."
        url="/campaigns"
      />
      
      {/* Active Campaigns Section */}
      <div id="active-campaigns" className="py-16 bg-white relative overflow-hidden">
        {/* Background elements */}
        <div className="absolute inset-0 opacity-[0.02]">
          <div 
            className="absolute inset-0" 
            style={{
              backgroundImage: `radial-gradient(#3B5BA5 1px, transparent 1px)`,
              backgroundSize: '40px 40px'
            }}
          ></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <h2 className="text-base text-primary font-semibold tracking-wide uppercase">Active Campaigns</h2>
            <p className="mt-2 text-4xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-5xl">
              Join These Live Campaigns
            </p>
            <p className="mt-4 max-w-2xl text-xl text-gray-500 mx-auto">
              Discover active review campaigns from top brands and start earning today
            </p>
          </motion.div>

          {loadingCampaigns ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
              <p className="ml-4 text-gray-600">Loading campaigns...</p>
            </div>
          ) : activeCampaigns.length > 0 ? (
            <ProjectShowcase
              items={activeCampaigns.map((c) => ({
                name: c.name,
                designation: c.category,
                quote: c.description,
                src: c.imageUrl,
                link: currentUser ? `/influencer/campaigns/${c.id}` : '/login',
              }))}
              autoPlayInterval={6000}
            />
          ) : (
            <div className="text-center py-12">
              <ShoppingBag className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Active Campaigns Found</h3>
              <p className="text-gray-500 mb-4">
                Check back soon for new campaign opportunities!
              </p>
              <p className="text-sm text-gray-400">
                New campaigns are added regularly by our brand partners.
              </p>
            </div>
          )}
          
          {/* View All Campaigns Link */}
          {activeCampaigns.length > 0 && (
            <motion.div 
              className="text-center mt-12"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.3 }}
            >
              <Link
                to={currentUser ? '/influencer/campaigns' : '/login'}
                className="inline-flex items-center px-6 py-3 border border-primary text-base font-medium rounded-md text-primary bg-white hover:bg-primary hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-all duration-300 cursor-pointer relative z-10"
                style={{ pointerEvents: 'auto' }}
              >
                View All Campaigns
              </Link>
            </motion.div>
          )}
        </div>
      </div>

      {/* Campaign Inspiration Hub Section */}
      <section className="py-20 bg-white">
        <motion.div 
          className="container mx-auto px-4"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={staggerContainer}
        >
          <motion.div variants={fadeInUp} className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold mb-6 text-gray-900">
              Campaign <span className="text-[#3B5BA5]">Inspiration Hub</span>
            </h2>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto">
              Discover trends, ideas, and strategies to create successful influencer marketing campaigns
            </p>
          </motion.div>
          
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div 
              variants={fadeInLeft}
              className="relative"
            >
              <div className="bg-gradient-to-br from-blue-100 to-purple-50 p-8 md:p-12 rounded-2xl shadow-xl">
                <div className="absolute -top-4 -right-4 w-20 h-20 bg-[#3B5BA5] opacity-10 rounded-full"></div>
                <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-[#3B5BA5] opacity-10 rounded-full"></div>
                
                <h3 className="text-2xl font-bold mb-4 text-gray-900">Campaign Inspiration Hub</h3>
                <p className="text-gray-700 mb-6">
                  Explore our comprehensive hub of campaign inspiration, trends, and best practices to help you create successful influencer marketing campaigns.
                </p>
              </div>
            </motion.div>
            
            <motion.div variants={fadeInRight}>
              <h3 className="text-2xl font-bold mb-6 text-gray-900">What to Expect</h3>
              
              <ul className="space-y-6">
                <li className="flex">
                  <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center rounded-full bg-blue-100 text-[#3B5BA5]">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                    </svg>
                  </div>
                  <div className="ml-4">
                    <h4 className="text-lg font-semibold text-gray-900">Creative Campaign Ideas</h4>
                    <p className="text-gray-700">Browse through a collection of creative campaign concepts that have proven successful.</p>
                  </div>
                </li>
                
                <li className="flex">
                  <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center rounded-full bg-blue-100 text-[#3B5BA5]">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
                    </svg>
                  </div>
                  <div className="ml-4">
                    <h4 className="text-lg font-semibold text-gray-900">Industry Insights</h4>
                    <p className="text-gray-700">Get access to the latest industry trends and data to inform your campaign strategy.</p>
                  </div>
                </li>
                
                <li className="flex">
                  <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center rounded-full bg-blue-100 text-[#3B5BA5]">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                  </div>
                  <div className="ml-4">
                    <h4 className="text-lg font-semibold text-gray-900">Influencer Matching</h4>
                    <p className="text-gray-700">Find the perfect influencers for your brand based on audience demographics and engagement metrics.</p>
                  </div>
                </li>
              </ul>
            </motion.div>
          </div>
        </motion.div>
      </section>

      {/* Trend Forecasting Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <h2 className="text-base text-primary font-semibold tracking-wide uppercase">Insights</h2>
            <p className="mt-2 text-4xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-5xl">
              Trend Forecasting
            </p>
            <p className="mt-4 max-w-2xl text-xl text-gray-500 mx-auto">
              Stay ahead of the curve with data-driven insights
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Left — text + checklist */}
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h3 className="text-xl font-medium text-gray-900 mb-4">Make data-backed decisions</h3>
              <p className="text-gray-500 mb-6 leading-relaxed">
                Our analytics engine tracks market shifts, consumer behavior, and campaign performance so you can plan campaigns that hit the right audience at the right time.
              </p>
              <ul className="space-y-3">
                {[
                  "Real-time market trend analysis",
                  "Consumer behavior deep dives",
                  "ROI and engagement tracking",
                ].map((item, index) => (
                  <motion.li
                    key={index}
                    className="flex items-center"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.15 }}
                    viewport={{ once: true }}
                  >
                    <div className="flex items-center justify-center w-5 h-5 rounded-full bg-primary/10 mr-3">
                      <Check className="w-3 h-3 text-primary" />
                    </div>
                    <span className="text-gray-700">{item}</span>
                  </motion.li>
                ))}
              </ul>
            </motion.div>

            {/* Right — visual dashboard mockup */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-blue-100 to-purple-100 rounded-2xl transform rotate-3"></div>
              <div className="relative bg-white p-6 rounded-2xl shadow-lg">
                {/* Dashboard header */}
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-primary" />
                    <h4 className="font-semibold text-gray-900">Analytics Dashboard</h4>
                  </div>
                  <div className="flex space-x-2">
                    <div className="w-2.5 h-2.5 rounded-full bg-red-400"></div>
                    <div className="w-2.5 h-2.5 rounded-full bg-yellow-400"></div>
                    <div className="w-2.5 h-2.5 rounded-full bg-green-400"></div>
                  </div>
                </div>

                {/* Stat cards */}
                <div className="grid grid-cols-3 gap-3 mb-4">
                  {[
                    { label: "Reach", value: "2.4M", icon: Users, color: "text-blue-600 bg-blue-50" },
                    { label: "Engagement", value: "8.7%", icon: TrendingUp, color: "text-green-600 bg-green-50" },
                    { label: "ROI", value: "3.2x", icon: DollarSign, color: "text-purple-600 bg-purple-50" },
                  ].map((stat, i) => {
                    const Icon = stat.icon;
                    return (
                      <div key={i} className={`${stat.color} rounded-lg p-3`}>
                        <Icon className="w-4 h-4 mb-1.5" />
                        <div className="text-lg font-bold text-gray-900">{stat.value}</div>
                        <div className="text-xs text-gray-500">{stat.label}</div>
                      </div>
                    );
                  })}
                </div>

                {/* Bar chart mockup */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="text-xs text-gray-400 mb-3">Monthly Performance</div>
                  <div className="flex items-end justify-between h-24 gap-2">
                    {[40, 65, 50, 80, 60, 90, 75].map((h, i) => (
                      <motion.div
                        key={i}
                        className="flex-1 bg-primary rounded-t"
                        initial={{ height: 0 }}
                        whileInView={{ height: `${h}%` }}
                        viewport={{ once: true }}
                        transition={{ delay: i * 0.08, duration: 0.5 }}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Inspiration Board Section */}
      <section className="py-20 bg-white">
        <motion.div 
          className="container mx-auto px-4"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={staggerContainer}
        >
          <motion.div variants={fadeInUp} className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold mb-6 text-gray-900">
              Inspiration <span className="text-[#3B5BA5]">Board</span>
            </h2>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto">
              Creative Campaign Concepts
            </p>
          </motion.div>
          
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div 
              variants={fadeInLeft}
              className="order-2 md:order-1"
            >
              <h3 className="text-2xl font-bold mb-6 text-gray-900">Curate Your Ideas</h3>
              <p className="text-gray-700 mb-6">
                Our inspiration board will help you collect, organize, and visualize your campaign ideas in one place. Collaborate with your team and influencers to create compelling campaigns that resonate with your audience.
              </p>
              
              <ul className="space-y-4">
                {[
                  "Save and organize campaign ideas",
                  "Collaborate with team members",
                  "Share concepts with influencers",
                  "Track campaign development progress"
                ].map((item, index) => (
                  <motion.li 
                    key={index}
                    className="flex items-center"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.2 }}
                    viewport={{ once: true }}
                  >
                    <svg className="h-5 w-5 text-[#3B5BA5] mr-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    <span>{item}</span>
                  </motion.li>
                ))}
              </ul>
              
              <motion.div 
                className="mt-8"
                variants={fadeInUp}
              >
                <div className="inline-flex items-center text-[#3B5BA5] font-medium">
                  <span>Get Started</span>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-2" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </div>
              </motion.div>
            </motion.div>
            
            <motion.div 
              variants={fadeInRight}
              className="order-1 md:order-2 relative"
            >
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-100 to-purple-100 rounded-2xl transform rotate-3"></div>
                <div className="relative bg-white p-6 rounded-2xl shadow-lg">
                  <div className="flex items-center justify-between mb-6">
                    <h4 className="font-bold text-lg">Campaign Inspiration</h4>
                    <div className="flex space-x-2">
                      <div className="w-3 h-3 rounded-full bg-red-400"></div>
                      <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                      <div className="w-3 h-3 rounded-full bg-green-400"></div>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    {[
                      { title: "Sustainable Fashion", color: "bg-green-100" },
                      { title: "Tech Product Launch", color: "bg-blue-100" },
                      { title: "Food & Beverage", color: "bg-yellow-100" },
                      { title: "Travel Experience", color: "bg-purple-100" }
                    ].map((item, index) => (
                      <motion.div 
                        key={index}
                        className={`p-3 ${item.color} rounded-lg`}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.15 }}
                        viewport={{ once: true }}
                      >
                        <div className="flex items-center">
                          <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center mr-3">
                            <span className="font-bold text-[#3B5BA5]">{index + 1}</span>
                          </div>
                          <div>
                            <h5 className="font-medium">{item.title}</h5>
                            <div className="h-2 w-24 bg-white rounded-full mt-1"></div>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                  
                  <div className="mt-6 flex justify-center">
                    <motion.div 
                      className="h-2 w-16 bg-gray-200 rounded-full"
                      animate={{ width: ["30%", "60%", "45%"] }}
                      transition={{ duration: 2, repeat: Infinity }}
                    ></motion.div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </section>

      {/* Campaign Varieties Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <h2 className="text-base text-primary font-semibold tracking-wide uppercase">Campaign Types</h2>
            <p className="mt-2 text-4xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-5xl">
              Campaign Varieties
            </p>
            <p className="mt-4 max-w-2xl text-xl text-gray-500 mx-auto">
              Explore the different ways you can collaborate with influencers
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                title: "Product Reviews",
                description: "Authentic product reviews from trusted influencers to build credibility and drive conversions.",
                icon: Star,
              },
              {
                title: "Brand Ambassadorships",
                description: "Long-term partnerships with influencers who embody your brand values and consistently promote your products.",
                icon: Users,
              },
              {
                title: "Content Collaborations",
                description: "Creative collaborations that integrate your brand into influencer content in an authentic and engaging way.",
                icon: Video,
              },
              {
                title: "Event Promotions",
                description: "Influencer attendance and coverage of your brand events to increase visibility and engagement.",
                icon: CalendarDays,
              },
              {
                title: "Social Media Takeovers",
                description: "Influencers take over your brand's social media accounts to provide fresh content and engage with your audience.",
                icon: Smartphone,
              },
              {
                title: "Affiliate Marketing",
                description: "Performance-based campaigns where influencers earn commission on sales generated through their unique links.",
                icon: Link2,
              },
            ].map((item, index) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={index}
                  className="group relative overflow-hidden rounded-2xl border border-gray-100 bg-white p-6 shadow-sm transition-all duration-300 hover:shadow-lg hover:-translate-y-1"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.08 }}
                >
                  {/* Icon */}
                  <div className="flex items-center gap-2 mb-4">
                    <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-primary/10">
                      <Icon className="w-4 h-4 text-primary" />
                    </div>
                    <h3 className="text-xl font-medium text-gray-900">{item.title}</h3>
                  </div>
                  <p className="text-gray-500">{item.description}</p>
                </motion.div>
              );
            })}
          </div>

          <motion.div
            className="text-center mt-12"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.3 }}
          >
            <Link
              to="/register"
              className="inline-flex items-center gap-2 px-6 py-3 border border-primary text-base font-medium rounded-md text-primary bg-white hover:bg-primary hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-all duration-300 cursor-pointer relative z-10"
              style={{ pointerEvents: 'auto' }}
            >
              Start Your Campaign
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default Campaigns;
